import type { ConfiguredAddOn } from '../types/addon.types';
import type { ConfiguredService } from '../types/service.types';

const UNLINKED_SELECTIONS = ['independent', 'eachservice'];

// A stored line is independent when null or the legacy literal 'independent';
// 'eachservice' never survives into a stored line, so it is excluded.
export function isIndependentLink(
    linkedServiceId: string | null | undefined
): linkedServiceId is null | undefined | 'independent' {
    return linkedServiceId === null || linkedServiceId === undefined || linkedServiceId === 'independent';
}

// Empty service list is left alone: QuoteValidationService::validateAddOns()
// only rejects an orphaned linkedServiceId while the quote still has services.
export function pruneOrphanedAddOns(
    addOns: ConfiguredAddOn[],
    services: ConfiguredService[]
): ConfiguredAddOn[] {
    if (services.length === 0) {
        return addOns;
    }

    const serviceIds = new Set(services.map((service) => service.instanceId));

    return addOns.filter((addOn) => {
        const { linkedServiceId } = addOn;
        if (!linkedServiceId || UNLINKED_SELECTIONS.includes(linkedServiceId)) {
            return true;
        }
        return serviceIds.has(linkedServiceId);
    });
}

// D11: re-key rather than prune, but only within one derivation
// generation — an unrelated change falls through to the prune above.
export function remapLinkedAddOns(
    addOns: ConfiguredAddOn[],
    previousRows: ConfiguredService[],
    nextRows: ConfiguredService[],
    previousGeneration: string,
    nextGeneration: string
): ConfiguredAddOn[] {
    if (previousGeneration !== nextGeneration || previousRows.length !== nextRows.length) {
        return pruneOrphanedAddOns(addOns, nextRows);
    }

    const idMap = new Map<string, string>();
    previousRows.forEach((row, index) => {
        idMap.set(row.instanceId, nextRows[index].instanceId);
    });

    return addOns.map((addOn) => {
        const { linkedServiceId } = addOn;
        if (!linkedServiceId || UNLINKED_SELECTIONS.includes(linkedServiceId)) {
            return addOn;
        }
        const remappedId = idMap.get(linkedServiceId);
        return remappedId ? { ...addOn, linkedServiceId: remappedId } : addOn;
    });
}

export function isLinkedServiceSelectable(
    linkedServiceId: string,
    services: { instanceId: string | number }[]
): boolean {
    if (linkedServiceId === '' || linkedServiceId === 'independent') {
        return true;
    }
    if (linkedServiceId === 'eachservice') {
        return services.length > 0;
    }
    return services.some((service) => service.instanceId === linkedServiceId);
}
